import React, {useState, useEffect} from 'react';
import axios from 'axios';
import '../members.scss';
import {AddEditMemberModal} from 'shared-one-ux-components';
import find from 'lodash/find';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import findIndex from 'lodash/findIndex';

export default function MembersEdit({
	member,
	isOpen,
	fetchMembers,
	onClose,
	memberRoles,
	ooid,
	adminView,
	allRoles,
	setToastData,
	roleDescriptions = [],
	setMembersList,
}) {
	const [allRolesMapped, setAllRolesMapped] = useState([]);
	const [loggedInRoles, setLoggedInRoles] = useState([]);
	const [irecBillingInfo, setIrecBillingInfo] = useState(null);
	const aoid = member.associateOID;

	useEffect(() => {
		// Silently pre-fetch iReceivables billing info so peCode can be passed directly
		// to the Step Function, bypassing the slow ADP billing API call (~20s) and keeping
		// total SF execution under API Gateway's 29s timeout.
		// Non-blocking: if the org has no billing info (isValid=false) or the call fails,
		// we proceed without peCode and the SF falls back to its own GetBillingInfo step.
		if (!isOpen) return;
		setIrecBillingInfo(null);
		const fetchIrecBillingInfo = async () => {
			try {
				const { data } = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/ireceivables/billing-info${
						adminView ? '?ooid='.concat(ooid) : ''
					}`
				);
				if (data?.isValid && data?.billingInfo?.peCode) {
					setIrecBillingInfo(data.billingInfo);
				}
			} catch {
				// Non-blocking — SF will fall back to GetBillingInfo if peCode is absent
			}
		};
		fetchIrecBillingInfo();
	}, [isOpen, ooid]);

	const fetchDetails = async(controller, setModalState) => {

		try {
			//getting member info to populate modal
			const {status, data: {member}} = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/members/${aoid}${adminView
						? '?ooid='.concat(ooid)
						: ''}`, {signal: controller.signal});

			if (status === 200) {
				return {
					...member, countryCode: (member.callingCode || '1').replace(/\D/g, '')
				};
			} else {
				setModalState('error');
			}

		} catch (error) {
			setModalState('error');
		}
	};

	const updateMember = async(dataToSend, fnameField, lnameField, getFullName) => {
		try {
			// Get member's current roles
			const currentRoleIds = new Set(
				(member.roles || []).map(r => r.roleId)
			);

			// Only process roles that represent ACTUAL changes
			const rolesToUpdate = (dataToSend.roles || [])
			.filter(role => {
				const roleId = role.id;
				const wasAssigned = currentRoleIds.has(roleId);
					
				// Only include if it's a real change:
				// - assigning a role that wasn't assigned before
				// - unassigning a role that was assigned before
				if (role.assign && !wasAssigned) return true;
				if (role.unassign && wasAssigned) return true;
				return false;
			})
			.map(role => {
				const roleId = role.id;
				const val = find(allRoles, {id: roleId});
				if (!val) {
					console.warn(`Role with id ${roleId} not found in allRoles`);
					return null;
				}
				return {
					id: roleId,
					roleCode: val.roleCode,
					...(role.assign && {assign: true}),
					...(role.unassign && {unassign: true})
				};
			})
			.filter(Boolean);

			const formatData = {
				firstName: dataToSend.firstName,
				lastName: dataToSend.lastName,
				email: dataToSend.email,
				emailId: dataToSend.emailId,
				phone: dataToSend.phone,
				callingCode: dataToSend.countryCode,
				phoneId: dataToSend.phoneId,
				...(adminView && {ooid}),
				roles: rolesToUpdate,
				// Optional: pass pre-fetched peCode so the iRec provisioning SF can skip
				// the slow ADP billing API call (~20s). Only included if the org has billing info.
				...(irecBillingInfo?.peCode && { peCode: irecBillingInfo.peCode }),
			};

			const {status} = await axios.put(
				`${import.meta.env.VITE_ROOT_PATH}/api/members/${member.associateOID}`,
				formatData
			);

			if (status === 200) {
				setToastData([
					true, {
						status: 'success',
						message: `Updated member ${getFullName(fnameField.value, lnameField.value)}!`
					}]);

				// Update member roles in local state
				const updatedRoles = [...(member.roles || [])];
						
				// Add newly assigned roles
				rolesToUpdate.filter(r => r.assign).forEach(newRole => {
					const roleDetail = find(allRoles, {id: newRole.id});
					if (roleDetail && !updatedRoles.find(r => r.roleId === newRole.id)) {
						updatedRoles.push({
						roleId: newRole.id,
						roleCode: newRole.roleCode,
						roleName: roleDetail.roleName,
						roleDescription: roleDetail.roleDescription
					});
					}
				});
				// Remove unassigned roles
				const unassignedIds = new Set(rolesToUpdate.filter(r => r.unassign).map(r => r.id));
				const finalRoles = updatedRoles.filter(r => !unassignedIds.has(r.roleId));

				setMembersList(members => {
					return members.map(m => {
						if (m.associateOID === member.associateOID) {
							return {
								...m,
								firstName: dataToSend.firstName,
								lastName: dataToSend.lastName,
								displayName: getFullName(dataToSend.firstName, dataToSend.lastName),
								initials: `${dataToSend.firstName[0]}${dataToSend.lastName[0]}`.toUpperCase(),
								roles: finalRoles
							};
						}
						return m;
					});
				});
				onClose();
			} else {
				setToastData([
					true, {
						status: 'error',
						message: `Unable to update member ${getFullName(fnameField.value, lnameField.value)}. Please try again!`
					}
				]);
				setTimeout(() => {
					onClose();
				}, 1000);
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', `Unable to update member ${getFullName(fnameField.value, lnameField.value)}. Please try again!`);
			setToastData([
				true, {
					status: 'error',
					message: errorMessage
				}
			]);
			setTimeout(() => {
				onClose();
			}, 1000);
		}
	};

	//member and all roles are the same
	useEffect(() => {
		if (isOpen) {
			const mappedAllRoles = allRoles.map((role) => {
				const val = find(roleDescriptions, {roleCode: role.roleCode});
				const newRoleName = val ? val.title : 'NA';
				return {
					roleID: role.id,
					roleCode: role.roleCode,
					roleName: newRoleName,
					roleDescription: role.roleDescription,
				};
			});

			const loggedInMemberRoles = memberRoles.map(
					(role) => {return {roleId: role.id};});
			setAllRolesMapped(mappedAllRoles);
			setLoggedInRoles(loggedInMemberRoles);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	return (<>
		{isOpen && allRolesMapped.length > 0 && <AddEditMemberModal
			isOpen={isOpen}
			onClose={onClose}
			mode="edit"
			member={member}
			getMemberInfoForUpdate={fetchDetails}
			updateMember={updateMember}
			allRoles={allRolesMapped}
			loggedInMember={{roles: loggedInRoles}}
			app="pss"
			setToastData={setToastData}
		/>}
	</>);
}
